/**
 * Fast Campaign Results Page
 * Shows magic link, JS snippet, and diffs after generation
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { copyToClipboard } from '@/lib/utils';
import { 
  Zap, 
  ArrowLeft,
  ExternalLink,
  Copy,
  CheckCircle2,
  Code,
  FileText,
  Loader2,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FastCampaignResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(`${API}/api/fast-campaigns/${id}`);
      setCampaign(response.data);
    } catch (error) {
      console.error('Error fetching fast campaign:', error);
      toast.error('Failed to load fast campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, copyId) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(copyId);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(null), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Fast campaign not found</p>
        </div>
      </div>
    );
  }

  // If campaign is draft, redirect to continue setup
  if (campaign.status === 'draft') {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/fast-campaigns')} className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Zap className="w-5 h-5 text-amber-500" />
              <h1 className="text-xl font-semibold">{campaign.name}</h1>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-6 text-center">
            <Zap className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Campaign Not Generated</h2>
            <p className="text-muted-foreground mb-6">
              This fast campaign hasn't been generated yet. Complete the setup to get your magic link.
            </p>
            <Button
              onClick={() => navigate(`/fast-campaigns/new?continue=${id}`)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Continue Setup
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // If generating
  if (campaign.status === 'generating') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Generating Your Campaign</h2>
          <p className="text-muted-foreground">This usually takes 10-20 seconds...</p>
        </div>
      </div>
    );
  }

  // If error
  if (campaign.status === 'error') {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/fast-campaigns')} className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Zap className="w-5 h-5 text-amber-500" />
              <h1 className="text-xl font-semibold">{campaign.name}</h1>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-600 mb-2">Generation Failed</h2>
            <p className="text-muted-foreground mb-4">
              {campaign.error_message || 'An error occurred during generation'}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/fast-campaigns')}
            >
              Back to Fast Campaigns
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Active campaign - show results
  const diffs = campaign.diffs || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/fast-campaigns')}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                <h1 className="text-xl font-semibold text-foreground">{campaign.name}</h1>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600 border border-green-500/20">
                <CheckCircle2 className="w-3 h-3" />
                Active
              </span>
            </div>
            {campaign.magic_link && (
              <a
                href={campaign.magic_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Preview
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Magic Link Section */}
        <div className="bg-surface border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <ExternalLink className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Magic Link</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Share this link to show your personalized version of the website.
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-muted/50 rounded-lg p-3 font-mono text-sm text-foreground overflow-x-auto">
              {campaign.magic_link}
            </div>
            <Button
              variant="outline"
              onClick={() => handleCopy(campaign.magic_link, 'magic-link')}
            >
              {copied === 'magic-link' ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* JS Snippet Section */}
        {campaign.js_snippet && (
          <div className="bg-surface border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Code className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Embed Script</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Add this script to your website to enable dynamic personalization.
            </p>
            <div className="relative">
              <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-sm">
                <code className="text-green-400">{campaign.js_snippet}</code>
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => handleCopy(campaign.js_snippet, 'js-snippet')}
              >
                {copied === 'js-snippet' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Diffs Section */}
        {diffs.length > 0 && (
          <div className="bg-surface border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Changes Applied</h2>
              <span className="text-sm text-muted-foreground">({diffs.length} fields)</span>
            </div>
            <div className="space-y-4">
              {diffs.map((diff, index) => (
                <div key={index} className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-muted/30 px-4 py-2 border-b border-border">
                    <span className="font-medium text-foreground">{diff.field}</span>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-border">
                    <div className="p-4">
                      <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Original</p>
                      <p className="text-sm text-foreground line-through opacity-60">{diff.original}</p>
                    </div>
                    <div className="p-4 bg-green-500/5">
                      <p className="text-xs text-green-600 mb-2 uppercase tracking-wide">New</p>
                      <p className="text-sm text-foreground">{diff.new}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Context Section */}
        {campaign.context_text && (
          <div className="bg-muted/30 border border-border rounded-xl p-6">
            <h3 className="font-medium text-foreground mb-2">Campaign Context</h3>
            <p className="text-sm text-muted-foreground">{campaign.context_text}</p>
          </div>
        )}
      </main>
    </div>
  );
}
